import { Component, EventEmitter, Inject, Input, NgZone, OnChanges, Output, SimpleChange } from "@angular/core";
import { ToastrService } from "ngx-toastr";
import { environment } from "../../../../environments/environment";
import { FileMetadata } from "../../../models/file-metadata";
import { Law } from "../../../models/laws";
import { LawControllerService } from "../../../services/laws-controller/law-controller.service";
import { ServiceControllerService } from "../../../services/service-controller/service-controller.service";
import {TranslatedBaseError} from "../TranslatedBaseError";
import {TranslateService} from "@ngx-translate/core";

@Component({
  selector: "law-new",
  templateUrl: "./law-new.component.html",
  styles: [],
  providers: [LawControllerService, ServiceControllerService, ToastrService],
})
export class LawNewComponent extends TranslatedBaseError implements OnChanges {

  @Input() public serviceId: string;
  public selectedLaw: Law;
  @Input() set law(value: Law) {
    this.selectedLaw = Object.assign({}, value);
  }
  @Output() public formSubmit = new EventEmitter();
  @Output() public editCancel = new EventEmitter();
  public downloadLink: string;
  public fileMetadata: FileMetadata;
  private baseUrl: string = environment.BASE_URL;
  public isLoading: boolean = false;
  public lawShortName: string = "";
  public lawName: any;

  constructor(toastr: ToastrService,
              protected serviceController: ServiceControllerService,
              @Inject(NgZone) protected zone: NgZone,
              private lawControllerService: LawControllerService,
              public translateService: TranslateService) {
    super(toastr, translateService);
  }

  public ngOnChanges(changes: { [propKey: string]: SimpleChange }) {
    if (changes.serviceId && changes.serviceId.currentValue) {
      this.serviceId = changes.serviceId.currentValue;
    }
  }

  public save(law: Law) {
    if (law.id === undefined) {
      this.createLaw(law);
    } else {
      this.saveLaw(law);
    }
  }

  public cancel() {
    this.formSubmit.emit({
      save: false, cancel: true,
    });
  }

  public createLaw(law: Law) {
    this.lawControllerService.createLaw(law).subscribe(
      (data) => {
        this.formSubmit.emit({ message: data, save: true, create: true });
        this.responseSuccessful("Law created!");
      },
      (error) => {
        if (error.error && error.error.errors) {
          this.showErrorMessage(error.error.errors[0].field + " " + error.error.errors[0].defaultMessage);
        } else {
          this.showErrorMessage(error);
        }
      },
    );
  }

  public saveLaw(law: Law) {
    if (!this.isLoading) {
      this.isLoading = true;
      this.lawControllerService.saveLaw(law).subscribe(
        () => {
          this.formSubmit.emit({
            save: true,
            law,
          });
          this.responseSuccessful("Law saved!");
        },
        (error) => {
          if (error.error && error.error.errors) {
            this.showErrorMessage(error.error.errors[0].field + " " + error.error.errors[0].defaultMessage);
          } else {
            this.showErrorMessage(error);
          }
        },
        () => this.isLoading = false,
      );
    }
  }

  public deleteLaw(isConfirmed) {
    if (isConfirmed) {
      this.lawControllerService.deleteLaw(this.selectedLaw.id).subscribe(
        () => {
          this.formSubmit.emit({ save: false, delete: true, law: undefined });
          this.responseSuccessful("Law deleted!");
        },
        (error) => this.showErrorMessageWithTitle("Message delete error!", error),
      );
    }
  }

  public isActive(selectedItem, item) {
    return selectedItem && selectedItem.id === item.id;
  }

  public uploadFileData() {
    if (this.selectedLaw && this.selectedLaw.fileId) {
      this.downloadLink = this.baseUrl + "file/" + this.selectedLaw.fileId + "/binary";
      this.getFileMetadata(this.selectedLaw.fileId);
    } else {
      this.downloadLink = undefined;
      this.fileMetadata = undefined;
    }
  }

  public getFileMetadata(fileId: string) {
    const getFileAction = this.serviceId ? this.serviceController.getServiceFile(this.serviceId, fileId) : this.serviceController.getFile(fileId);
    getFileAction.subscribe(
        (data: FileMetadata) => this.fileMetadata = data,
        (error) => this.showErrorMessageWithTitle("Get file error!", error),
    );
  }

  public download(item) {
    const downloadAction = this.serviceId ? this.serviceController.downloadServiceFile(this.serviceId, item.fileId) : this.serviceController.downloadFile(item.fileId);
    return downloadAction.subscribe((res) => {
      const pdfUrl = (window.URL || (window as any).webkitURL).createObjectURL(new Blob([res], { type: res.type }));
      const anchor = document.createElement("a");
      anchor.href = pdfUrl;
      anchor.setAttribute("download", item.fileId);
      anchor.click();
      // this.onChange.emit();
    });
  }

  public handleUpload(fileId: string, item) {
    // if previous exists then lets remove
    if (item.fileId) {
      const deleteAction = this.serviceId ? this.serviceController.deleteServiceFile(this.serviceId, item.fileId) : this.serviceController.deleteFile(item.fileId);
      deleteAction.subscribe(
          () => {
            item.fileId = undefined;
          },
          () => null,
          () => fileId ? this.saveFile(fileId, item) : null,
      );
    } else if (fileId) {
      this.saveFile(fileId, item);
    }
  }

  private saveFile(fileId: string, item) {
    setTimeout(() => {
      this.zone.run(() => {
        if (fileId) {
          item.fileId = fileId;
          this.uploadFileData();
        }
      });
    });
  }

  public deleteFile(item) {
    const deleteAction = this.serviceId ? this.serviceController.deleteServiceFile(this.serviceId, item.fileId) : this.serviceController.deleteFile(item.fileId);
    deleteAction.subscribe(
      () => {
        delete item.fileId;
        this.lawControllerService.saveLaw(item).subscribe(() => undefined);
      },
      (error) => this.showErrorMessageWithTitle("File delete error!", error),
    );
  }

  public closeLayer() {
    this.editCancel.emit({ cancel: true });
  }

  public nameChanged(value): void {
    this.lawShortName = value;
    this.selectedLaw.shortName = this.lawShortName.substring(0, 20);
  }

  public isNew(item) {
    return item === undefined;
  }
}
